import type { CommonAction } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, DisabledReq, type Xiaohongshu } from "../../types";
import { executeAction } from "../../utils";
import { CollectionDetail } from "../getXhsCollection";
import type { GroupDetail } from "../getXhsGroup";
import { mockAction } from "./mock";
import { rpaAction } from "./rpa";
import { rpaAction_Server } from "./rpa-server";
import { rpaAction_Server_Mock } from "./rpa-server-mock";

const FictionalRendition = z.object({
    type: z.literal("fictional-rendition"), // 虚拟演绎，仅供娱乐
});
const AIGenerated = z.object({
    type: z.literal("ai-generated"), // 笔记含AI合成内容
});
const SourceStatement = z.object({
    type: z.literal("source-statement"), // 内容来源声明
    childType: z.enum(["self-labeling", "self-shooting", "transshipment"]), // 已自主标注 / 自助拍摄 / 转载
    shootingLocation: z.custom<Xiaohongshu.Location>().optional(), // 拍摄地点
    shootingDate: z.string().optional(), // 拍摄日期
    sourceMedia: z.string().optional(), // 来源媒体
});
const SelfDeclarationSchema = z.union([FictionalRendition, AIGenerated, SourceStatement]);

type SelfDeclaration = z.infer<typeof SelfDeclarationSchema>;

export const XiaohongshuPublishParamsSchema = ActionCommonParamsSchema.extend({
    banners: z.array(z.string()), // 图片
    title: z.string(), // 标题
    content: z.string(), // 正文
    address: z.custom<Xiaohongshu.Location>().optional(), // 地点
    selfDeclaration: z.custom<SelfDeclaration>().optional(), // 自主声明
    topic: z.array(z.custom<Xiaohongshu.Topic>()).optional(), //话题
    proxyLoc: z.string().optional(), // 代理位置
    localIP: z.string().optional(), // 用户ip
    huiwenToken: z.string().optional(), // 绘文token
    visibleRange: z.enum(["public", "private", "friends"]), // 可见范围
    isImmediatelyPublish: z.boolean().optional(), // 是否立即发布
    scheduledPublish: z.string().optional(), // 定时发布时间
    collectionId: z.string().optional(), // 合集ID TODO：迁移完成后该字段移除
    collectionBind: z.custom<CollectionDetail>().optional(), // 合集绑定
    groupBind: z.custom<GroupDetail>().optional(), // 群聊绑定
    noteCopyBind: z.boolean().optional(), // 是否允许复制
    coProduceBind: z.boolean().optional(), // 是否允许合拍
    originalBind: z.boolean().optional(), // 是否开启原创声明, 声明检测app端展示，web端发布后，最终是否原创成功以小红书APP结果为准
});

export type XiaohongshuPublishParams = z.infer<typeof XiaohongshuPublishParamsSchema>;

export type PublishAction = CommonAction<XiaohongshuPublishParams, string>;

export const xiaohongshuPublish: PublishAction = async (task, params) => {
    task.logger.info(`xiaohongshuPublish actionType: ${params.actionType}`);
    if (DisabledReq) {
        return {
            code: 414,
            data: "",
            message:
                "临时维护：小红书发布政策调整，本平台小红书发布功能临时暂停服务并进行维护调整，暂时无法正常发布，敬请理解知悉。",
        };
    }
    if (params.actionType === "rpa") {
        return rpaAction(task, params);
    }

    if (params.actionType === "mockApi") {
        return mockAction(task, params);
    }

    if (params.actionType === "server") {
        return rpaAction_Server(task, params);
    }

    // 优先执行Mock+RPA方案，失败则进入RPA-Server方案
    return executeAction(mockAction, rpaAction_Server_Mock, rpaAction_Server)(task, params);
};
